import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, driverApi, escortApi } from '../api/mockApi';

const ROUTE_STATUSES = ['ON ROUTE', 'OFF ROUTE'];
const DEFAULT_RADIUS = 2;

const EMPTY_FILTERS = {
  name: '',
  area: '',
  routeName: '',
  vehicle: '',
  routeStatus: '',
};

// createdAt DESC (newest first) — mirrors the server default ordering.
const byNewest = (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);

const formatMiles = (miles) => {
  const n = Number(miles);
  if (!isFinite(n)) return '?';
  return n.toFixed(1);
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ areas: [], routeNames: [], vehicles: [], statuses: ROUTE_STATUSES });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedName, setDebouncedName] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [pas, setPas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [radiusInput, setRadiusInput] = useState(String(DEFAULT_RADIUS));
  const navigate = useNavigate();

  const filtersActive = Boolean(
    filters.name || filters.area || filters.routeName || filters.vehicle || filters.routeStatus
  );

  // ── Stats + filter option lists (once) ────────────────────────────
  useEffect(() => {
    const load = async () => {
      const [statsRes, filtersRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getFilters(),
      ]);
      if (statsRes && statsRes.success) setStats(statsRes.data || {});
      if (filtersRes && filtersRes.success) {
        const d = filtersRes.data || {};
        setOptions({
          areas: Array.isArray(d.areas) ? d.areas : [],
          routeNames: Array.isArray(d.routeNames) ? d.routeNames : [],
          vehicles: Array.isArray(d.vehicles) ? d.vehicles : [],
          statuses: Array.isArray(d.statuses) && d.statuses.length ? d.statuses : ROUTE_STATUSES,
        });
      }
      setLoading(false);
    };
    load();
  }, []);

  // ── Debounce the free-text name field (~350ms) ────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(filters.name), 350);
    return () => clearTimeout(t);
  }, [filters.name]);

  // ── Filter-driven fetch of drivers + PAs ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setListLoading(true);
      const shared = {
        name: debouncedName,
        area: filters.area,
        routeStatus: filters.routeStatus,
        routeName: filters.routeName,
      };
      const [driversRes, pasRes] = await Promise.all([
        // vehicle only applies to drivers
        driverApi.getAll({ ...shared, vehicle: filters.vehicle, include: 'pa' }),
        escortApi.getAll(shared),
      ]);
      if (cancelled) return;
      setDrivers(driversRes && driversRes.success && Array.isArray(driversRes.data)
        ? [...driversRes.data].sort(byNewest)
        : []);
      setPas(pasRes && pasRes.success && Array.isArray(pasRes.data)
        ? [...pasRes.data].sort(byNewest)
        : []);
      setListLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [debouncedName, filters.area, filters.vehicle, filters.routeStatus, filters.routeName]);

  // ── Route coverage / nearest-available ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setCoverageLoading(true);
      const res = await dashboardApi.getCoverage(radius, 3);
      if (cancelled) return;
      if (res && res.success) {
        const d = res.data || {};
        setCoverage({
          radiusMiles: Number(d.radiusMiles) || radius,
          uncovered: Array.isArray(d.uncovered) ? d.uncovered : [],
          covered: Array.isArray(d.covered) ? d.covered : [],
        });
      } else {
        setCoverage({ radiusMiles: radius, uncovered: [], covered: [] });
      }
      setCoverageLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [radius]);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setDebouncedName('');
  };

  const applyRadius = useCallback(() => {
    const parsed = parseFloat(radiusInput);
    const next = isFinite(parsed) ? Math.min(25, Math.max(0.5, parsed)) : DEFAULT_RADIUS;
    setRadiusInput(String(next));
    setRadius(next);
  }, [radiusInput]);

  const s = stats || {};
  const totalDrivers = s.totalDrivers != null ? s.totalDrivers : drivers.length;
  const totalPAs = s.totalEscorts != null ? s.totalEscorts : pas.length;
  const activeRadius = coverage ? coverage.radiusMiles : radius;

  const driverRows = filtersActive ? drivers : drivers.slice(0, 5);
  const paRows = filtersActive ? pas : pas.slice(0, 5);

  const renderNearest = (person, kind) => {
    if (!person) return null;
    const within = Boolean(person.withinRadius);
    const sub = [person.area, kind === 'driver' ? person.vehicle : null, person.routeStatus]
      .filter(Boolean)
      .join(' · ');
    return (
      <div
        key={`${kind}-${person.id}`}
        className="nearest-item"
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(`/${kind === 'driver' ? 'drivers' : 'escorts'}/${person.id}`)}
      >
        <div className="who">
          <span className="nearest-name">{person.fullName || 'Unnamed'}</span>
          <span className="nearest-sub">{sub || '—'}</span>
        </div>
        <span className={`distance-badge ${within ? 'within' : 'outside'}`}>
          {formatMiles(person.distanceMiles)} mi
        </span>
      </div>
    );
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;

  return (
    <div>
      <h1 className="page-title">Dashboard Overview</h1>

      {/* ── Stat cards ─────────────────────────────────────────────── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{s.totalDrivers ?? 0}</div>
          <div className="stat-label">Total Drivers</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{s.totalEscorts ?? 0}</div>
          <div className="stat-label">Total PAs</div>
        </div>
        <div className="stat-card on-route">
          <div className="stat-value">{s.driversOnRoute ?? 0}</div>
          <div className="stat-label">Drivers ON ROUTE</div>
        </div>
        <div className="stat-card off-route">
          <div className="stat-value">{s.driversOffRoute ?? 0}</div>
          <div className="stat-label">Drivers OFF ROUTE</div>
        </div>
        <div className="stat-card on-route">
          <div className="stat-value">{s.escortsOnRoute ?? 0}</div>
          <div className="stat-label">PAs ON ROUTE</div>
        </div>
        <div className="stat-card off-route">
          <div className="stat-value">{s.escortsOffRoute ?? 0}</div>
          <div className="stat-label">PAs OFF ROUTE</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{s.totalRoutes ?? 0}</div>
          <div className="stat-label">Total Routes</div>
        </div>
        <div className="stat-card on-route">
          <div className="stat-value">{s.routesCovered ?? 0}</div>
          <div className="stat-label">Routes Covered</div>
        </div>
        <div className="stat-card off-route">
          <div className="stat-value">{s.routesUncovered ?? 0}</div>
          <div className="stat-label">Routes Uncovered</div>
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────── */}
      <div className="filters-bar">
        <div className="filter-field">
          <label htmlFor="f-name">Name</label>
          <input
            id="f-name"
            value={filters.name}
            placeholder="Search name…"
            onChange={e => setFilter('name', e.target.value)}
          />
        </div>
        <div className="filter-field">
          <label htmlFor="f-area">Area</label>
          <select id="f-area" value={filters.area} onChange={e => setFilter('area', e.target.value)}>
            <option value="">All areas</option>
            {options.areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="f-route">Route</label>
          <select id="f-route" value={filters.routeName} onChange={e => setFilter('routeName', e.target.value)}>
            <option value="">All routes</option>
            {options.routeNames.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="f-vehicle">Vehicle</label>
          <select id="f-vehicle" value={filters.vehicle} onChange={e => setFilter('vehicle', e.target.value)}>
            <option value="">All vehicles</option>
            {options.vehicles.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="f-status">Route Status</label>
          <select id="f-status" value={filters.routeStatus} onChange={e => setFilter('routeStatus', e.target.value)}>
            <option value="">All statuses</option>
            {options.statuses.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
        <div className="filters-actions">
          <button className="btn btn-outline btn-sm" onClick={clearFilters} disabled={!filtersActive}>
            Clear filters
          </button>
          <span className="filter-summary">
            {listLoading
              ? 'Filtering…'
              : `Showing ${drivers.length} of ${totalDrivers} drivers · ${pas.length} of ${totalPAs} PAs`}
          </span>
        </div>
      </div>

      {/* ── Route coverage & nearest available ─────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 className="section-title">Route Coverage &amp; Nearest Available</h2>
        <div className="radius-control" style={{ marginBottom: 16 }}>
          <label htmlFor="f-radius" style={{ fontSize: 13, color: 'var(--text-light)' }}>Radius (miles)</label>
          <input
            id="f-radius"
            type="number"
            min="0.5"
            max="25"
            step="0.5"
            value={radiusInput}
            onChange={e => setRadiusInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyRadius(); }}
          />
          <button className="btn btn-outline btn-sm" onClick={applyRadius}>Apply</button>
        </div>
      </div>

      {coverageLoading && <div className="empty-state">Calculating nearest available staff…</div>}

      {!coverageLoading && coverage && coverage.uncovered.length === 0 && (
        <div className="empty-state">Every route has a driver assigned. 🎉</div>
      )}

      {!coverageLoading && coverage && coverage.uncovered.length > 0 && (
        <div className="coverage-grid">
          {coverage.uncovered.map((item, idx) => {
            const route = (item && item.route) || {};
            const nearestDrivers = Array.isArray(item?.nearestDrivers) ? item.nearestDrivers : [];
            const nearestEscorts = Array.isArray(item?.nearestEscorts) ? item.nearestEscorts : [];
            const hasAny = nearestDrivers.length > 0 || nearestEscorts.length > 0;
            const closest = nearestDrivers[0] || nearestEscorts[0];
            return (
              <div className="coverage-card" key={route.id || `uncovered-${idx}`}>
                <h4
                  style={{ cursor: route.id ? 'pointer' : 'default' }}
                  onClick={() => { if (route.id) navigate(`/routes/${route.id}`); }}
                >
                  {route.name || 'Unnamed route'}
                </h4>
                <div className="coverage-meta">
                  {[route.area, route.postCode].filter(Boolean).join(' · ') || 'Location unknown'}
                  {' · needs '}
                  {route.vehicleRequired || 'any vehicle'}
                </div>

                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--danger)', marginBottom: 10 }}>
                  No driver assigned
                </div>

                {!hasAny && (
                  <div className="empty-state">
                    No candidates with a known location. Add post codes to driver/PA profiles to enable
                    distance matching.
                  </div>
                )}

                {hasAny && !item.hasCandidateWithinRadius && closest && (
                  <div className="empty-state">
                    Nobody within {formatMiles(activeRadius)} miles — nearest shown below is{' '}
                    {formatMiles(closest.distanceMiles)} miles away.
                  </div>
                )}

                {nearestDrivers.length > 0 && (
                  <>
                    <div className="nearest-sub" style={{ margin: '10px 0 6px', fontWeight: 700 }}>
                      Nearest Drivers
                    </div>
                    {nearestDrivers.map(d => renderNearest(d, 'driver'))}
                  </>
                )}

                {nearestEscorts.length > 0 && (
                  <>
                    <div className="nearest-sub" style={{ margin: '10px 0 6px', fontWeight: 700 }}>
                      Nearest PAs
                    </div>
                    {nearestEscorts.map(e => renderNearest(e, 'escort'))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!coverageLoading && coverage && coverage.covered.length > 0 && (
        <div className="coverage-grid">
          {coverage.covered.map((item, idx) => {
            const route = (item && item.route) || {};
            return (
              <div className="coverage-card ok" key={route.id || `covered-${idx}`}>
                <h4
                  style={{ cursor: route.id ? 'pointer' : 'default' }}
                  onClick={() => { if (route.id) navigate(`/routes/${route.id}`); }}
                >
                  {route.name || 'Unnamed route'}
                </h4>
                <div className="coverage-meta">
                  {[route.area, route.postCode].filter(Boolean).join(' · ') || 'Location unknown'}
                </div>
                <div className="nearest-item" style={{ marginBottom: 0 }}>
                  <div className="who">
                    <span className="nearest-name">{item.driverName || 'Driver assigned'}</span>
                    <span className="nearest-sub">
                      {item.escortName ? `PA: ${item.escortName}` : 'No PA assigned'}
                    </span>
                  </div>
                  <span className="pill">Covered</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Drivers ────────────────────────────────────────────────── */}
      <h2 className="section-title">{filtersActive ? 'Drivers' : 'Recent Drivers'}</h2>
      <table className="data-table" style={{ marginBottom: 32 }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Area</th>
            <th>Route</th>
            <th>Vehicle</th>
            <th>Mobile</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {driverRows.length === 0 && (
            <tr>
              <td colSpan={6} className="empty-state">
                {listLoading ? 'Loading drivers…' : 'No drivers match these filters.'}
              </td>
            </tr>
          )}
          {driverRows.map(d => (
            <tr key={d.id} onClick={() => navigate(`/drivers/${d.id}`)} style={{ cursor: 'pointer' }}>
              <td>
                {d.fullName || 'N/A'}
                {d.pa && d.pa.fullName && (
                  <div className="nearest-sub">PA: {d.pa.fullName}</div>
                )}
              </td>
              <td>{d.area || '—'}</td>
              <td>{d.routeName || '—'}</td>
              <td>{d.vehicle || 'N/A'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{d.mobileNumber}</td>
              <td>
                <span className={`status-badge ${d.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`}>
                  <span className="dot"></span>
                  {d.routeStatus}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Passenger Assistants ───────────────────────────────────── */}
      <h2 className="section-title">{filtersActive ? 'Passenger Assistants' : 'Recent Passenger Assistants'}</h2>
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Area</th>
            <th>Route</th>
            <th>Mobile</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {paRows.length === 0 && (
            <tr>
              <td colSpan={5} className="empty-state">
                {listLoading ? 'Loading passenger assistants…' : 'No passenger assistants match these filters.'}
              </td>
            </tr>
          )}
          {paRows.map(e => (
            <tr key={e.id} onClick={() => navigate(`/escorts/${e.id}`)} style={{ cursor: 'pointer' }}>
              <td>
                {e.fullName || 'N/A'}
                {e.belongsToDriverName && (
                  <div className="nearest-sub">Driver: {e.belongsToDriverName}</div>
                )}
              </td>
              <td>{e.area || '—'}</td>
              <td>{e.routeName || '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{e.mobileNumber}</td>
              <td>
                <span className={`status-badge ${e.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`}>
                  <span className="dot"></span>
                  {e.routeStatus}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DashboardPage;
